import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Power, Trash2, Loader2, User, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Header, Field, inputCls, saveCls } from "@/components/admin/ServicesTab";
import PaymentPolicyField, { inheritedPolicy } from "@/components/admin/PaymentPolicyField";

export default function BarbersTab({ barbers, services, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", service_ids: [], active: true, photoFile: null, removePhoto: false, photo_url: null, payment_policy: inheritedPolicy });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ name: "", service_ids: [], active: true, photoFile: null, removePhoto: false, photo_url: null, payment_policy: inheritedPolicy }); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ name: b.name, service_ids: b.service_ids || [], active: b.active, photoFile: null, removePhoto: false, photo_url: b.photo_url || null, payment_policy: b.payment_policy || inheritedPolicy }); setEditId(b.id); setOpen(true); };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do profissional.");
    if (form.service_ids.length === 0) return toast.error("Selecione ao menos um serviço.");
    setSaving(true);
    try {
      const payload = { name: form.name, service_ids: form.service_ids, active: form.active, payment_policy: form.payment_policy };
      const { data: saved } = editId ? await api.put(`/admin/barbers/${editId}`, payload) : await api.post("/admin/barbers", payload);
      if (form.removePhoto && editId) await api.delete(`/admin/barbers/${editId}/photo`);
      if (form.photoFile) {
        const image = new FormData(); image.append("file", form.photoFile);
        await api.post(`/admin/barbers/${saved.id}/photo`, image, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editId ? "Profissional atualizado." : "Profissional cadastrado.");
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/admin/barbers/${b.id}`, { name: b.name, service_ids: b.service_ids, active: !b.active, payment_policy: b.payment_policy });
      reload();
    } catch { toast.error("Erro ao alterar status."); }
  };

  const remove = async (b) => {
    try {
      const { data } = await api.delete(`/admin/barbers/${b.id}`);
      toast.success(data.deleted ? "Profissional excluído." : data.message);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir."); }
  };

  const serviceName = (id) => services.find((s) => s.id === id)?.name || "—";

  return (
    <div className="fade-in">
      <Header title="Profissionais" onNew={openNew} testid="new-barber-btn" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {barbers.map((b) => (
          <div key={b.id} data-testid={`barber-item-${b.id}`} className={`card-surface p-5 ${!b.active ? "opacity-55" : ""}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center overflow-hidden">
                {b.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${b.photo_url}`} alt={`Foto de ${b.name}`} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#D4AF37]" />}
              </div>
              <div>
                <h3 className="font-sans2 font-semibold text-amber-50">{b.name} {!b.active && <span className="text-xs text-stone-500">(inativo)</span>}</h3>
                <p className="text-xs text-stone-400">{(b.service_ids || []).map(serviceName).join(", ")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Btn testid={`edit-barber-${b.id}`} onClick={() => openEdit(b)} icon={Pencil} label="Editar" />
              <Btn testid={`toggle-barber-${b.id}`} onClick={() => toggleActive(b)} icon={Power} label={b.active ? "Desativar" : "Ativar"} />
              <Btn testid={`delete-barber-${b.id}`} onClick={() => remove(b)} icon={Trash2} label="Excluir" danger />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#12141A] border-[#282C37]">
          <DialogHeader><DialogTitle className="font-display text-amber-100">{editId ? "Editar profissional" : "Novo profissional"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome">
              <input data-testid="barber-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <div>
              <label className="text-sm text-stone-300 font-sans2 mb-2 block">Foto de perfil <span className="text-stone-500">(opcional)</span></label>
              {form.photo_url && !form.removePhoto && !form.photoFile && <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${form.photo_url}`} alt="Foto atual" className="mb-3 h-16 w-16 rounded-full object-cover border border-[#282C37]" />}
              {form.photoFile && <p data-testid="selected-barber-photo" className="mb-2 text-xs text-stone-400">Nova foto: {form.photoFile.name}</p>}
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#D4AF37]/40 px-3 py-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10"><ImagePlus className="w-4 h-4" />{form.photo_url ? "Alterar foto" : "Adicionar foto"}<input data-testid="barber-photo-input" type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={(event) => setForm({ ...form, photoFile: event.target.files?.[0] || null, removePhoto: false })} /></label>
                {form.photo_url && !form.removePhoto && <button data-testid="remove-barber-photo" type="button" onClick={() => setForm({ ...form, photoFile: null, removePhoto: true })} className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" />Remover foto</button>}
                {form.removePhoto && <button type="button" onClick={() => setForm({ ...form, removePhoto: false })} className="text-sm text-stone-400">Manter foto atual</button>}
              </div>
              <p className="mt-2 text-xs text-stone-500">PNG, JPG/JPEG ou WEBP, até 2 MB.</p>
            </div>
            <div>
              <label className="text-sm text-stone-300 font-sans2 mb-2 block">Serviços realizados</label>
              <div className="space-y-2">
                {services.map((s) => (
                  <label key={s.id} data-testid={`barber-service-${s.id}`} className="flex items-center gap-2.5 text-sm text-stone-200 cursor-pointer">
                    <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} className="accent-[#D4AF37] w-4 h-4" />
                    {s.name} <span className="text-stone-500 text-xs">({s.duration} min)</span>
                  </label>
                ))}
              </div>
            </div>
            <PaymentPolicyField value={form.payment_policy} onChange={(payment_policy) => setForm({ ...form, payment_policy })} />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-stone-400 font-sans2">Cancelar</button>
            <button data-testid="save-barber-btn" onClick={save} disabled={saving} className={saveCls}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Btn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-sans2 transition-colors ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-[#282C37] text-stone-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
